import getAPIDomain from '@signal/utils/env/apiDomain'
import isDevelopment from '@signal/utils/env/isDevelopment'
import isTest from '@signal/utils/env/isTest'
import isStorybook from '@signal/utils/env/isStorybook'
import isOnSite from '@signal/utils/env/onSite'
import { postData } from './swr'

const SUBST_EXTRACT_REGEX = /{([^}]+)}/g
const MSEC_WAIT_FOR_ERROR_BOUNDARY = 500
const MSEC_RESET_ERROR_MESSAGE = 1000

export type FrontEndLogEntry = {
  level: string
  template: string
  kvp: Record<string, unknown>
}

/*
 * FrontEndLogger which will eventually send logging to BE (future behavior)
 */
class FrontEndLogger {
  //
  // member variables
  //

  // the name of the app, for logging purposes
  _projectName: string
  // the set of messages which have been sent (mainly for unit test purposes)
  _messagesSent: Array<FrontEndLogEntry> = []
  // the most recent error that was logged
  _lastErrorMessage: string | undefined = undefined

  _IGNORE_REGEXES: Array<RegExp> = [
    /401 error/,
    /Redirecting to default report/
  ]

  constructor(projectName: string) {
    this._projectName = projectName
  }

  //
  // public methods below this line
  //

  /*
   * error - send an error log message
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   *   (see generateLoggingKeyValuePairs for details)
   */
  public error(template: string, ...args: Array<unknown>) {
    this.sendLogToBackend('error', false, template, ...args)
  }

  /*
   * error - send a warning log message
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   *   (see generateLoggingKeyValuePairs for details)
   */
  public warning(template: string, ...args: Array<unknown>) {
    this.sendLogToBackend('warning', false, template, ...args)
  }

  /*
   * error - send an information log message
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   *   (see generateLoggingKeyValuePairs for details)
   */
  public info(template: string, ...args: Array<unknown>) {
    this.sendLogToBackend('information', false, template, ...args)
  }

  /*
   * exception - send details on an exception as an error log message
   * @param {Error} ex - the exception to be logged
   * @param {string} componentStack - if an error boundary caught this exception, this is a nicer stack provided by React
   */
  public exception(ex: Error, componentStack?: string | null) {
    // if componentStack is provided, then this exception came from an error boundary
    // use it immediately
    if (componentStack) {
      const message = ex.message
      const stack = componentStack || ex.stack
      // @ts-ignore
      const url = ex.url ?? ''
      // we might see the same exception trip multiple times.  Don't log them multiply
      if (message !== this._lastErrorMessage) {
        this.sendLogToBackend(
          'error',
          true /* isException */,
          'unhandled FE exception: {message} {stack} {error_url}',
          message,
          stack,
          url
        )
        this._lastErrorMessage = message
      }

      // wait a period of time before allowing an exception with the same message to be logged again
      setTimeout(() => {
        if (this._lastErrorMessage === message) {
          this._lastErrorMessage = undefined
        }
      }, MSEC_RESET_ERROR_MESSAGE)
    } else {
      // if no component stack, wait to see if this gets handled by an error boundary
      // which will have a nicer stack
      setTimeout(() => {
        this.exception(ex, ex.stack)
      }, MSEC_WAIT_FOR_ERROR_BOUNDARY)
    }
  }

  /*
   * purge - reset the messages sent (for unit test purposes)
   */
  public purge() {
    this._messagesSent = []
  }

  /*
   * getMessages - used to get the set of sent log messages (for unit test purposes)
   * @return Array<FrontEndLogEntry> - the set of log messages which have been sent since the last purge
   */
  public getMessages(): Array<FrontEndLogEntry> {
    return this._messagesSent
  }

  //
  // private methods below this line
  //

  /*
   * sendLogToBackend - core function for sending logging to the backend
   * @param {string} level - the level of the message (error, warning, information)
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   *   (see generateLoggingKeyValuePairs for details)
   */
  private sendLogToBackend(
    level: string,
    isException: boolean,
    template: string,
    ...args: Array<unknown>
  ) {
    const kvp = this.dictionaryFromLogParams(template, ...args)

    this._messagesSent.push({
      level,
      template,
      kvp
    })

    console.log(
      `[LOG ${this._projectName}]`,
      this.hydrateTemplateFromLogParams(template, ...args)
    )

    if (
      !isDevelopment() &&
      !isTest() &&
      !isStorybook() &&
      (isException || !isOnSite())
    ) {
      const payload = this.payloadFromLogParams(template, ...args)

      // bail if this payload matches an ignore regex
      if (this._IGNORE_REGEXES.some(regex => regex.test(payload))) {
        return
      }

      postData({
        body: payload,
        url: `${getAPIDomain('/dev-ops/api')}/v1/fe-logging/${level}`
      }).catch(() => {})
    }
  }

  /*
   * dictionaryFromLogParams - generates KVP to go with the log message
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   * @return {Record<string, unknown>} - the key value pairs which go with the template
   *   example: if the template is "Dealer {dealerName} had {salesCount} sales last {timeSpan}", the arguments should be
   *     "Coggin Honda", 108, "month", and the resulting kvp would be:
   *     { dealerName: "Coggin Honda", salesCount: 108, timeSpan: "month" }
   */
  private dictionaryFromLogParams(
    template: string,
    ...args: Array<unknown>
  ): Record<string, unknown> {
    const keys = template.match(SUBST_EXTRACT_REGEX) || []
    return keys.reduce((kvp, key, keyOn) => {
      kvp[key.substr(1, key.length - 2)] = args[keyOn]
      return kvp
    }, {} as Record<string, unknown>)
  }

  /*
   * payloadFromLogParams - generates the POST payload from the log message
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   * @return {string} - the POST payload
   */
  private payloadFromLogParams(
    template: string,
    ...args: Array<unknown>
  ): string {
    return JSON.stringify({
      ApplicationName: this._projectName,
      ApplicationPath: window.location.href,
      MessageTemplate: template,
      PropertyValues: args
    })
  }

  /*
   * hydrateTemplateFromLogParams - takes a log template and turns it into a hydrated log string
   * @param {string} template - the template string for the message
   * @param {varies} ...args - arguments which should pair up with the embedded strings inside the template
   * @return {string} - the template with the params substituted inside
   */
  private hydrateTemplateFromLogParams(
    template: string,
    ...args: Array<unknown>
  ): string {
    let argOn = 0
    let match: RegExpExecArray | null
    const regex = new RegExp(SUBST_EXTRACT_REGEX)
    while ((match = regex.exec(template)) !== null) {
      const beforeStr = template.substr(0, match.index)
      const afterStr = template.substr(match.index + match[0].length)
      template = `${beforeStr}${args[argOn++]}${afterStr}`
    }
    return template
  }
}

let _logger: typeof FrontEndLogger.prototype | null = null

/*
 * initLogger - initializes the global logger
 * @param {string} projectName - the name of the application that is going to be logging
 */
export function initLogger(projectName: string) {
  if (_logger && !isTest() && !isStorybook()) {
    // it's global, so it should only be initialized once
    throw new Error('logger was already initialized')
  }
  _logger = new FrontEndLogger(projectName)
}

/*
 * getLogger - initializes the logger
 * @return {FrontEndLogger} the logger singleton instance
 */
export function getLogger(): FrontEndLogger {
  if (!_logger && (isTest() || isStorybook())) {
    // this should not be permitted in a real app scenario
    initLogger('dummy')
  }
  return _logger!
}

export default getLogger
